
import { Component, Vue, Watch } from 'vue-property-decorator'
import Project from "@/types/project/project"
import Tracking from "@/types/tracking/tracking"
import { TimesService } from "@/services/timetrack/TimesService"
import { TrackingService } from "@/services/timetrack/TrackingService"
import FlipClock from "@/components/FlipClock.vue"
import StartingTime from "@/components/StartingTime.vue"
import ExpenditureDialog from "@/components/Expenditure/ExpenditureDialog.vue"
import { ExpenditureService } from "@/services/timetrack/ExpenditureService"
import CompleteTracking from "@/components/Tracking/UpdateTracking.vue"
import TrackingFields from "@/components/Tracking/TrackingFields.vue"
import { ProjectService } from "@/services/timetrack/ProjectService"
import { DateAndTimeHelper } from "@/helpers/DateAndTimeHelper"

@Component({
  components: {
    TrackingFields,
    CompleteTracking,
    ExpenditureDialog,
    FlipClock,
    StartingTime
  }
})
export default class DashboardTracking extends Vue {
  private timeService = new TimesService()
  private projectService = new ProjectService()
  private trackingService = new TrackingService()
  private expenditureService = new ExpenditureService()
  private dateTimeHelper = new DateAndTimeHelper()
  private completeTrackingDialog = false
  private makingInterval: any | null = null
  private currentMaking: string | null = null
  private startedAt: number | null = null
  private trackingsLoading = false
  private currentTracking: Tracking | null = null
  private selectedProjectToTrack: Project | null = null
  private tasks = [this.$t('timetrack.time.example_task')]
  private task: string | null = null
  private projects: Project[] = []

  @Watch('startedAt')
  private startTimer () {
    if (this.startedAt && !this.currentTracking?.endTime) {
      clearInterval(this.makingInterval)
      this.makingInterval = setInterval(() => {
        this.updateMaking()
      }, 1000)
    } else {
      clearInterval(this.makingInterval)
      this.updateMaking()
    }
  }

  created () {
    this.fetchData()
  }

  private fetchData () {
    this.$emit('projectCreated')
    this.fetchProjects()
      .then((projects) => {
        this.projects = projects
        this.fetchTracking()
      })
  }

  private get trackingButtonDisabled () {
    if (this.currentTracking && !this.currentTracking.endTime) {
      return false
    }
    if (!this.currentTracking) {
      return false
    }
    return true
  }

  private startOrStopTimeTracking () {
    if (this.currentTracking) {
      if (!this.selectedProjectToTrack || !this.task) {
        this.startedAt = null
        clearInterval(this.makingInterval)
        this.saveEndTimeToCurrentTracking()
      }
      this.stopTimeTracking()
    } else {
      this.startTimeTracking()
    }
  }

  private startTimeTracking () {
    if (this.selectedProjectToTrack && this.selectedProjectToTrack.id) {
      this.trackingService.startTimeWithProject(this.selectedProjectToTrack.id)
        .then(() => {
          this.$store.dispatch('project/setHasRunningTracking', { running: true, projectId: this.selectedProjectToTrack?.id })
          this.startTimer()
        })
        .catch((error) => {
          this.$store.dispatch('error/setError', error)
        })
    } else {
      this.trackingService.startTime()
        .then(() => {
          clearInterval(this.makingInterval)
        })
        .catch((error) => {
          this.$store.dispatch('error/setError', error)
        })
    }
    this.fetchTracking()
  }

  private updateMaking () {
    if (this.selectedProjectToTrack && this.selectedProjectToTrack.salary && this.startedAt) {
      const projectSalary = this.selectedProjectToTrack.salary
      const difference = Math.abs((this.currentTracking?.endTime || this.dateTimeHelper.getRoundedDateTimeToMore(new Date())) - this.startedAt)
      this.currentMaking = (((projectSalary / 60) / 60) * (difference / 1000)).toFixed(2)
    }
  }

  private stopTimeTracking () {
    if (this.currentTracking &&
      this.currentTracking.id &&
      this.selectedProjectToTrack &&
      this.selectedProjectToTrack.id &&
      this.task && this.currentMaking) {
      this.timeService.storeTime({
        startingTime: Number(this.currentTracking.startingTime),
        endTime: this.currentTracking.endTime || this.dateTimeHelper.getRoundedDateTimeToMore(new Date())
      }, this.selectedProjectToTrack.id, this.task, Number(this.currentMaking)).then((trackingId: string) => {
        if (this.currentTracking && this.currentTracking.id) {
          this.trackingService.removeCurrentTracking(this.currentTracking.id)
          this.expenditureService.updateExpendituresTrackingId(this.currentTracking.id, trackingId)
        }
      }).then(() => {
        this.$store.dispatch('project/setHasRunningTracking', { running: false, projectId: this.selectedProjectToTrack?.id })
        this.resetTracking()
      }).catch((error) => {
        this.$store.dispatch('error/setError', error)
      })
    }
  }

  private resetTracking () {
    this.startedAt = null
    this.task = null
    this.selectedProjectToTrack = null
    this.currentMaking = null
    this.currentTracking = null
    this.completeTrackingDialog = false
  }

  private fetchProjects () {
    return this.projectService.getAllProjects()
      .then((projects) => {
        return projects as Project[]
      }).catch((error) => {
        return this.$store.dispatch('error/setError', error)
      })
  }

  private fetchTracking () {
    this.trackingsLoading = true
    this.trackingService.fetchCurrentTracking()
      .then((tracking) => {
        if (tracking) {
          this.currentTracking = tracking as unknown as Tracking
          this.task = this.currentTracking.task
          this.startedAt = this.currentTracking.startingTime
          if (this.currentTracking.projectId) {
            this.selectedProjectToTrack =
              this.projects.find((project) => { return project.id === this.currentTracking?.projectId }) || null
            this.updateMaking()
          } else {
            this.selectedProjectToTrack = null
            this.currentMaking = null
          }
          this.fetchTasks()
          this.fetchExpenditures()
          if (!this.currentTracking.endTime) this.startTimer()
        } else {
          this.resetTracking()
        }
        this.completeTrackingDialog = !!this.currentTracking?.endTime
      }).catch((error) => {
        this.$store.dispatch('error/setError', error)
      }).finally(() => {
        this.trackingsLoading = false
      })
  }

  private fetchTasks () {
    this.trackingsLoading = true
    this.timeService.getAllTasks()
      .then((tasks) => {
        if (tasks) {
          this.tasks = tasks
        }
      }).catch((error) => {
        this.$store.dispatch('error/setError', error)
      }).finally(() => {
        this.trackingsLoading = false
      })
  }

  private fetchExpenditures () {
    if (!this.currentTracking || !this.currentTracking.id) return
    this.trackingsLoading = true
    this.expenditureService.getExpendituresForTracking(this.currentTracking.id)
      .then((expenditures) => {
        if (this.currentTracking && expenditures && expenditures.length) {
          this.currentTracking.expenditures = expenditures
        }
      }).catch((error) => {
        this.$store.dispatch('error/setError', error)
      }).finally(() => {
        this.trackingsLoading = false
      })
  }

  private dateOrTimeChanged (date: Date) {
    if (!this.currentTracking || !this.currentTracking.id || !date) {
      return
    }
    this.trackingsLoading = true
    this.trackingService.updateDateTime(this.currentTracking.id, date.getTime())
      .then(() => {
        this.fetchTracking()
      }).catch((error) => {
        this.$store.dispatch('error/setError', error)
      }).finally(() => {
        this.trackingsLoading = false
      })
  }

  private endDateOrTimeChanged (date: Date) {
    if (!this.currentTracking || !this.currentTracking.id || !date) {
      return
    }
    this.trackingsLoading = true
    this.trackingService.updateEndingDateTime(this.currentTracking.id, date.getTime())
      .then(() => {
        this.fetchTracking()
      }).catch((error) => {
        this.$store.dispatch('error/setError', error)
      }).finally(() => {
        this.trackingsLoading = false
      })
  }

  private saveEndTimeToCurrentTracking () {
    if (!this.currentTracking || !this.currentTracking.id) return
    const roundedTime = this.dateTimeHelper.getRoundedDateTimeToMore()
    this.trackingService.endTime(this.currentTracking.id, roundedTime)
      .then(() => {
        this.fetchTracking()
        this.completeTrackingDialog = true
      }).catch((error) => {
        this.$store.dispatch('error/setError', error)
      }).finally(() => {
        this.trackingsLoading = false
      })
  }
}
